import { DateTime } from 'luxon';

/**
 * Convert ISO string to local timezone
 * @param {string} isoUtc - ISO string (e.g., "2025-10-20T14:15:00Z" or "2025-10-20T14:15:00")
 * @param {string} iana - IANA timezone (e.g., "America/Chicago")
 * @returns {DateTime} Luxon DateTime object in local timezone
 */
export function toLocal(isoUtc, iana) {
  if (!isoUtc) return null;
  const tz = iana || 'UTC';
  
  // Check if this is a date-only string (YYYY-MM-DD)
  // These should be treated as local dates, not UTC midnight
  if (typeof isoUtc === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(isoUtc)) {
    return DateTime.fromISO(isoUtc, { zone: tz });
  }
  
  // Check if string has timezone info (Z or +/-offset) - convert from that timezone
  if (typeof isoUtc === 'string' && (isoUtc.includes('Z') || isoUtc.match(/[+-]\d{2}:\d{2}$/))) {
    return DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz);
  }
  
  // For naive datetimes (no timezone), check if it's user-entered or system-generated:
  // - User-entered: YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS (no microseconds)
  // - System-generated: has microseconds (e.g., 2025-12-20T03:58:29.672000)
  if (typeof isoUtc === 'string') {
    // Has microseconds (6 digits after decimal) = system-generated UTC timestamp
    if (isoUtc.match(/\.\d{6}$/)) {
      return DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz);
    }
    // Has milliseconds (3 digits after decimal) = also likely system-generated
    if (isoUtc.match(/\.\d{3}$/)) {
      return DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz);
    }
    // No fractional seconds = user-entered local time (meeting times, voting periods)
    return DateTime.fromISO(isoUtc, { zone: tz });
  }
  
  // Fallback - parse as UTC and convert
  return DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz);
}

/**
 * Convert naive local datetime string to UTC ISO string
 * @param {string} localString - Naive datetime string (e.g., "2025-10-20T19:30")
 * @param {string} tz - IANA timezone
 * @returns {string} ISO UTC string
 */
export function toUtcString(localString, tz) {
  if (!localString) return null;
  return DateTime.fromISO(localString, { zone: tz || 'UTC' }).toUTC().toISO();
}

/**
 * Format a UTC ISO string for display in local timezone
 * @param {string} isoUtc - ISO string in UTC
 * @param {string} iana - IANA timezone
 * @param {string} format - Luxon format string (default: "MMM d, yyyy h:mm a ZZZZ")
 * @returns {string} Formatted datetime string
 */
export function formatLocal(isoUtc, iana, format = 'MMM d, yyyy h:mm a ZZZZ') {
  const dt = toLocal(isoUtc, iana);
  return dt ? dt.toFormat(format) : '';
}

/**
 * Get current datetime in a specific timezone
 * @param {string} iana - IANA timezone
 * @returns {DateTime} Current datetime in specified timezone
 */
export function nowInTimezone(iana) {
  return DateTime.now().setZone(iana || 'UTC');
}
